import { create } from 'zustand';
import { supabase, supabaseConfigured } from '@/lib/supabase';

/**
 * Session state, backed by Supabase Auth (Google is the only enabled
 * provider). `status` starts at 'loading' so route guards can wait for the
 * session restore before deciding to redirect - without that, a signed-in
 * user reloading a protected page would flash through a logged-out state.
 *
 * Without Supabase configured (`supabaseConfigured` false), every visitor is
 * a permanent guest: sign-in is a no-op and status resolves straight to
 * 'guest'. That is the expected state until VITE_SUPABASE_URL and
 * VITE_SUPABASE_ANON_KEY are set and Google is enabled in the Supabase
 * project's Auth settings.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'guest';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  avatarUrl: string | null;
}

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** Read by the API client to attach `Authorization: Bearer <token>`. */
  accessToken: string | null;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

function userFrom(
  supabaseUser: { id: string; email?: string; user_metadata?: Record<string, unknown> } | null,
): AuthUser | null {
  if (!supabaseUser?.email) return null;
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name: (supabaseUser.user_metadata?.name as string | undefined) ?? null,
    avatarUrl: (supabaseUser.user_metadata?.avatar_url as string | undefined) ?? null,
  };
}

export const useAuthStore = create<AuthState>(() => ({
  status: supabaseConfigured ? 'loading' : 'guest',
  user: null,
  accessToken: null,

  signInWithGoogle: async () => {
    if (!supabaseConfigured) return;
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
  },

  signOut: async () => {
    if (!supabaseConfigured) return;
    await supabase.auth.signOut();
  },
}));

if (supabaseConfigured) {
  supabase.auth.getSession().then(({ data }) => {
    useAuthStore.setState({
      status: data.session ? 'authenticated' : 'guest',
      user: userFrom(data.session?.user ?? null),
      accessToken: data.session?.access_token ?? null,
    });
  });

  supabase.auth.onAuthStateChange((_event, session) => {
    useAuthStore.setState({
      status: session ? 'authenticated' : 'guest',
      user: userFrom(session?.user ?? null),
      accessToken: session?.access_token ?? null,
    });
  });
}
